import React from 'react';
import { TrendingUp, DollarSign, BarChart, Award, Globe } from 'lucide-react';

/**
 * Компонент для отображения текущих трендов крипторынка в России
 */
const MarketTrends = () => {
  // В реальном приложении данные загружались бы из API
  const marketData = {
    lastUpdated: '18 апреля 2025 г.',
    trends: [
      {
        name: 'Bitcoin (BTC)',
        priceRub: 4823650,
        change24h: 2.3,
        volume24h: 1250000000,
        isPopular: true
      },
      {
        name: 'Ethereum (ETH)',
        priceRub: 238790,
        change24h: 3.1,
        volume24h: 750000000,
        isPopular: true
      },
      {
        name: 'Binance Coin (BNB)',
        priceRub: 47850,
        change24h: 1.2,
        volume24h: 320000000,
        isPopular: true
      },
      {
        name: 'Solana (SOL)',
        priceRub: 14590,
        change24h: 4.7,
        volume24h: 180000000,
        isPopular: true
      },
      {
        name: 'XRP',
        priceRub: 55.20,
        change24h: -1.8,
        volume24h: 95000000,
        isPopular: false
      }
    ],
    russianMarket: {
      mostPopular: ['BTC', 'ETH', 'USDT', 'BNB', 'SOL'],
      avgMonthlyGrowth: 2.8,
      totalVolume: 9500000000,
      tradingPairs: 'RUB/USDT, RUB/BTC, RUB/ETH'
    },
    activeExchanges: [
      { name: 'Binance', status: 'Доступна через VPN', popularity: 'Высокая' },
      { name: 'ByBit', status: 'Доступна через VPN', popularity: 'Средняя' },
      { name: 'OKX', status: 'Доступна через VPN', popularity: 'Средняя' },
      { name: 'Garantex', status: 'Работает в России*', popularity: 'Средняя' },
      { name: 'Kucoin', status: 'Доступна через VPN', popularity: 'Средняя' }
    ]
  };

  // Форматирование числа с разделителями и знаками после запятой
  const formatNumber = (value, decimals = 0) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  // Форматирование валюты
  const formatCurrency = (value, currency = '₽') => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value) + ' ' + currency;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <TrendingUp className="w-6 h-6 text-indigo-500" />
        Текущие тренды крипторынка в России
        <span className="text-sm text-gray-500 dark:text-gray-400 font-normal ml-auto">
          Обновлено: {marketData.lastUpdated}
        </span>
      </h2>
      
      <div className="space-y-6">
        {/* Популярные криптовалюты в России */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Популярные криптовалюты в России
          </h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Криптовалюта
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Цена (₽)
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Изменение (24ч)
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Объем торгов (₽)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {marketData.trends.filter(coin => coin.isPopular).map((coin, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {coin.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {formatCurrency(coin.priceRub)}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${
                      coin.change24h >= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {coin.change24h >= 0 ? '+' : ''}{coin.change24h}%
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400">
                      {formatCurrency(coin.volume24h)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Особенности российского крипторынка */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-100 dark:border-indigo-800">
            <h4 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-2 flex items-center gap-2">
              <BarChart className="w-4 h-4 text-indigo-500" />
              Текущие показатели рынка
            </h4>
            <ul className="space-y-2 text-sm text-indigo-700 dark:text-indigo-300">
              <li className="flex justify-between">
                <span>Самые популярные монеты:</span>
                <span className="font-medium">{marketData.russianMarket.mostPopular.join(', ')}</span>
              </li>
              <li className="flex justify-between">
                <span>Средний месячный рост:</span>
                <span className="font-medium text-green-600 dark:text-green-400">+{marketData.russianMarket.avgMonthlyGrowth}%</span>
              </li>
              <li className="flex justify-between">
                <span>Суточный объем торгов:</span>
                <span className="font-medium">{formatCurrency(marketData.russianMarket.totalVolume)}</span>
              </li>
              <li className="flex justify-between">
                <span>Популярные торговые пары:</span>
                <span className="font-medium">{marketData.russianMarket.tradingPairs}</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-100 dark:border-purple-800">
            <h4 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-2">
              <Globe className="w-4 h-4 text-purple-500" />
              Биржи, популярные в России
            </h4>
            <div className="overflow-y-auto max-h-44">
              <table className="min-w-full divide-y divide-purple-200 dark:divide-purple-800">
                <thead>
                  <tr>
                    <th className="text-left text-xs font-medium text-purple-700 dark:text-purple-300 py-1">Биржа</th>
                    <th className="text-left text-xs font-medium text-purple-700 dark:text-purple-300 py-1">Статус</th>
                    <th className="text-left text-xs font-medium text-purple-700 dark:text-purple-300 py-1">Популярность</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-100 dark:divide-purple-900">
                  {marketData.activeExchanges.map((exchange, index) => (
                    <tr key={index} className="text-xs text-purple-700 dark:text-purple-300">
                      <td className="py-1.5 font-medium">{exchange.name}</td>
                      <td className="py-1.5">{exchange.status}</td>
                      <td className="py-1.5">{exchange.popularity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-purple-500 dark:text-purple-400 italic">
              * Обратите внимание: использование некоторых сервисов может потребовать VPN. Всегда проверяйте актуальный правовой статус.
            </p>
          </div>
        </div>
        
        {/* Рекомендации для российских инвесторов */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700 text-sm">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            Рекомендации для российских инвесторов
          </h4>
          <ul className="space-y-1 text-gray-600 dark:text-gray-300 list-disc ml-5">
            <li>Используйте надежные и проверенные VPN-сервисы при работе с зарубежными биржами</li>
            <li>Внимательно изучите правила верификации на выбранных платформах</li>
            <li>Рассмотрите возможность использования P2P-платформ для покупки и продажи криптовалют</li>
            <li>Следите за изменениями российского законодательства в области регулирования криптовалют</li>
            <li>Учитывайте комиссии при выводе средств через российские банки</li>
          </ul>
        </div>
        
        <div className="text-xs text-gray-500 dark:text-gray-400 italic">
          Примечание: данные рыночных трендов приведены исключительно в информационных целях и не являются 
          инвестиционной рекомендацией. Всегда проводите собственное исследование рынка перед принятием 
          инвестиционных решений.
        </div>
      </div>
    </div>
  );
};

export default MarketTrends;